import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency, formatPct } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Target, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    above: "bg-primary/10 text-primary border-primary/30",
    near: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    below: "bg-destructive/10 text-destructive border-destructive/30",
    no_target: "bg-muted text-muted-foreground border-border",
};

export default function SalesTargetsPage() {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const [period, setPeriod] = useState(currentMonth);
    const [perf, setPerf] = useState(null);
    const [species, setSpecies] = useState([]);
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [targets, setTargets] = useState([]);
    const [dlg, setDlg] = useState(null);

    const load = async () => {
        const [p, s, t, f] = await Promise.all([
            api.get(`/sales-performance?period=${period}`).then(r => r.data),
            api.get("/species").then(r => r.data),
            api.get("/sales-targets").then(r => r.data),
            api.get("/farm").then(r => r.data),
        ]);
        setPerf(p); setSpecies(s); setTargets(t); setFarm(f);
    };
    useEffect(() => { load(); }, [period]);

    const save = async () => {
        if (dlg.id) await api.put(`/sales-targets/${dlg.id}`, dlg);
        else await api.post("/sales-targets", dlg);
        toast.success("Target saved"); setDlg(null); load();
    };
    const del = async (t) => { if (window.confirm("Delete target?")) { await api.delete(`/sales-targets/${t.id}`); load(); } };

    return (
        <div className="space-y-6" data-testid="sales-targets-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Target className="w-8 h-8 text-primary" />
                    <div>
                        <div className="tiny-label">{i18n.t("batch3.63c904559993")}</div>
                        <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.d3ac55072811")}</h1>
                    </div>
                </div>
                <div className="flex gap-3">
                    <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="h-12" data-testid="period-picker"/>
                    <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                        <DialogTrigger asChild>
                            <Button className="h-12" onClick={() => setDlg({ scope: "farm", species_id: "", period, revenue_target: 0, units_target: 0, profit_target: 0 })} data-testid="add-target-btn">
                                <Plus className="w-4 h-4 mr-2" /> {i18n.t("batch3.67dc05bac4fb")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch3.6f98cfd4f8fd")}</DialogTitle></DialogHeader>
                            {dlg && <div className="space-y-4">
                                <div><Label className="tiny-label">{i18n.t("batch3.4651a34e4df9")}</Label>
                                    <Select value={dlg.scope} onValueChange={v => setDlg({...dlg, scope: v})}>
                                        <SelectTrigger className="h-12 mt-2" data-testid="target-scope-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="farm">{i18n.t("batch3.b0bc4d15fe08")}</SelectItem>
                                            <SelectItem value="species">{i18n.t("batch3.e765a0a52295")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {dlg.scope === "species" && (
                                    <div><Label className="tiny-label">{i18n.t("batch3.83a7238826ab")}</Label>
                                        <Select value={dlg.species_id} onValueChange={v => setDlg({...dlg, species_id: v})}>
                                            <SelectTrigger className="h-12 mt-2" data-testid="target-species-select"><SelectValue placeholder={i18n.t("batch3.1f77e83907c4")} /></SelectTrigger>
                                            <SelectContent>{species.map(s => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div><Label className="tiny-label">{i18n.t("batch3.33d7d03e71d4")}</Label>
                                    <Input type="month" value={dlg.period} onChange={e => setDlg({...dlg, period: e.target.value})} className="h-12 mt-2"/>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="tiny-label">{i18n.t("batch3.07a02d4abd0b")}</Label>
                                        <Input type="number" value={dlg.revenue_target} onChange={e => setDlg({...dlg, revenue_target: e.target.value})} className="h-12 mt-2" data-testid="target-revenue-input"/>
                                    </div>
                                    <div><Label className="tiny-label">{i18n.t("batch3.7f8966640d8f")}</Label>
                                        <Input type="number" value={dlg.units_target} onChange={e => setDlg({...dlg, units_target: e.target.value})} className="h-12 mt-2"/>
                                    </div>
                                    <div className="col-span-2"><Label className="tiny-label">{i18n.t("batch3.a1b26b4530ec")}</Label>
                                        <Input type="number" value={dlg.profit_target} onChange={e => setDlg({...dlg, profit_target: e.target.value})} className="h-12 mt-2"/>
                                    </div>
                                </div>
                                <Button onClick={save} className="h-12 w-full" data-testid="save-target-btn">{i18n.t("batch3.efc007a393f6")}</Button>
                            </div>}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {perf && (
                <Card className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                        <div>
                            <div className="tiny-label">{i18n.t("batch3.6bdb25798a64")} {perf.period}</div>
                            <div className="kpi-value text-4xl font-bold mt-2">{formatCurrency(perf.farm_actual, farm.currency)} <span className="text-lg text-muted-foreground">/ {formatCurrency(perf.farm_target, farm.currency)}</span></div>
                        </div>
                        <div className="text-right">
                            <div className="tiny-label">{i18n.t("batch3.19130d3f1b9d")}</div>
                            <div className={`kpi-value text-4xl font-bold mt-2 ${perf.farm_achievement_pct >= 100 ? "text-primary" : perf.farm_achievement_pct >= 80 ? "text-amber-600" : "text-destructive"}`}>{formatPct(perf.farm_achievement_pct)}</div>
                        </div>
                    </div>
                    <Progress value={Math.min(perf.farm_achievement_pct, 100)} className="h-3" data-testid="farm-progress"/>
                    <div className="mt-3 text-sm text-muted-foreground">
                        {i18n.t("batch3.30ad4167acea")} <span className="font-mono font-semibold">{formatCurrency(perf.farm_remaining, farm.currency)}</span>
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {perf?.species?.map(row => (
                    <Card key={row.species_id} className="p-5 border rounded-2xl" data-testid={`species-target-${row.species_name.toLowerCase()}`}>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-11 h-11 rounded-xl grid place-items-center text-2xl" style={{backgroundColor: (row.color || "#eee") + "40"}}>{row.icon}</div>
                            <div className="flex-1">
                                <div className="font-bold text-lg">{row.species_name}</div>
                                <span className={`inline-block text-xs px-2 py-0.5 rounded-md border ${STATUS_STYLES[row.status]}`}>{row.status.replace("_"," ")}</span>
                            </div>
                        </div>
                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">{i18n.t("batch3.61ad50a9b918")}</span><span className="font-mono">{formatCurrency(row.target, farm.currency)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">{i18n.t("batch3.7bb2ee60f7c8")}</span><span className="font-mono font-semibold">{formatCurrency(row.actual, farm.currency)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">{i18n.t("batch3.b1a3b1aee034")}</span><span className="font-mono">{row.units_sold}{row.units_target ? ` / ${row.units_target}` : ""}</span></div>
                        </div>
                        <Progress value={Math.min(row.achievement_pct, 100)} className="h-2 mt-3"/>
                        <div className="text-xs text-right mt-1 font-semibold">{formatPct(row.achievement_pct)}</div>
                    </Card>
                ))}
            </div>

            <Card className="p-4">
                <h3 className="text-xl font-semibold mb-3">{i18n.t("batch3.f41107c4fcd2")}</h3>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{i18n.t("batch3.170a28a9db6d")}</TableHead>
                            <TableHead>{i18n.t("batch3.4651a34e4df9")}</TableHead>
                            <TableHead>{i18n.t("batch3.83a7238826ab")}</TableHead>
                            <TableHead className="text-right">{i18n.t("batch3.07a02d4abd0b")}</TableHead>
                            <TableHead className="text-right">{i18n.t("batch3.12748281e0f0")}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {targets.map(t => (
                            <TableRow key={t.id}>
                                <TableCell className="font-mono">{t.period}</TableCell>
                                <TableCell>{t.scope}</TableCell>
                                <TableCell>{species.find(s => s.id === t.species_id)?.name || "—"}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(t.revenue_target, farm.currency)}</TableCell>
                                <TableCell className="text-right font-mono">{t.units_target || "—"}</TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Button size="sm" variant="outline" onClick={() => setDlg(t)}>{i18n.t("batch3.5301648dcf6b")}</Button>
                                    <Button size="sm" variant="destructive" onClick={() => del(t)}>{i18n.t("batch3.717e1e93af99")}</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {targets.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">{i18n.t("batch3.728acc0ce486")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
